"use client";

import { useMessagePart } from "@assistant-ui/react";
import type { SyntaxHighlighterProps } from "@assistant-ui/react-markdown";
import { FC, useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  RadialLinearScale,
  Title,
  Tooltip
} from "chart.js";
import { Bar, Doughnut, Line, Pie, PolarArea, Radar, Scatter } from "react-chartjs-2";

import { applyChartDefaults } from "./chart-utils";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  RadialLinearScale
);

/**
 * Props for the ChartjsDiagram component
 */
export type ChartjsDiagramProps = SyntaxHighlighterProps & {
  className?: string;
};

/**
 * ChartjsDiagram component for rendering Chart.js charts with automatic defaults
 * Use it by passing to `componentsByLanguage` for chartjs in `markdown-text.tsx`
 *
 * This component automatically applies frontend defaults for chart styling, colors, and options.
 * The backend only needs to provide minimal data.
 *
 * Expected JSON configuration format (minimal backend data):
 * {
 *   "type": "line" | "bar" | "pie" | "doughnut" | "radar" | "polarArea" | "scatter",
 *   "data": {
 *     "labels": ["Jan", "Feb", "Mar", ...],
 *     "datasets": [
 *       {
 *         "label": "Series Name",
 *         "data": [value1, value2, value3, ...]
 *       }
 *     ]
 *   },
 *   "options": {
 *     "title": { "display": true, "text": "Chart Title" },
 *     "scales": {
 *       "x": { "title": { "display": true, "text": "X Axis Label" } },
 *       "y": { "title": { "display": true, "text": "Y Axis Label" } }
 *     }
 *   }
 * }
 *
 * The component will automatically add:
 * - Color schemes for datasets
 * - Responsive settings
 * - Default styling (borders, fill, tension, etc.)
 * - Grid settings
 * - Legend positioning
 * - Point styling for line charts
 * - And more chart-type specific defaults
 *
 * @example
 * const MarkdownTextImpl = () => {
 *   return (
 *     <MarkdownTextPrimitive
 *       remarkPlugins={[remarkGfm]}
 *       className="aui-md"
 *       components={defaultComponents}
 *       componentsByLanguage={{
 *         chartjs: {
 *           SyntaxHighlighter: ChartjsDiagram
 *         },
 *       }}
 *     />
 *   );
 * };
 */
export const ChartjsDiagram: FC<ChartjsDiagramProps> = ({
  code,
  className,
  node: _node,
  components: _components,
  language: _language,
}) => {
  console.log(code, className, _node, _components, _language);
  const [chartConfig, setChartConfig] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Detect when this code block is complete
  const isComplete = useMessagePart((part) => {
    if (part.type !== "text") return false;

    // Find the position of this code block
    const codeIndex = part.text.indexOf(code);
    if (codeIndex === -1) return false;

    // Check if there are closing backticks immediately after this code block
    const afterCode = part.text.substring(codeIndex + code.length);

    // Look for the closing backticks - should be at the start or after a newline
    const closingBackticksMatch = afterCode.match(/^```|^\n```/);
    return closingBackticksMatch !== null;
  });

  useEffect(() => {
    if (!isComplete) return;

    try {
      // Parse the code as JSON to get Chart.js configuration
      const rawConfig = JSON.parse(code);

      // Validate required properties
      if (!rawConfig.type || !rawConfig.data) {
        throw new Error(
          "Chart configuration must include 'type' and 'data' properties"
        );
      }

      // Validate chart type
      const validTypes = [
        "line",
        "bar",
        "pie",
        "doughnut",
        "radar",
        "polarArea",
        "scatter",
      ];
      if (!validTypes.includes(rawConfig.type)) {
        throw new Error(
          `Invalid chart type: ${
            rawConfig.type
          }. Supported types: ${validTypes.join(", ")}`
        );
      }

      // Apply defaults to the configuration
      const enhancedConfig = applyChartDefaults(rawConfig);

      setChartConfig(enhancedConfig);
      setError(null);
    } catch (e) {
      console.warn("Failed to parse Chart.js configuration:", e);
      setError(
        e instanceof Error ? e.message : "Invalid Chart.js configuration"
      );
      setChartConfig(null);
    }
  }, [isComplete, code]);

  // Render the appropriate chart component based on type
  const renderChart = () => {
    if (error) {
      return (
        <div className="p-4 text-center text-red-600">
          <div className="mb-2 font-semibold">Chart Error</div>
          <div className="text-sm">{error}</div>
        </div>
      );
    }

    return loadChart(chartConfig);
  };

  return (
    <div className={cn("rounded-b-lg bg-muted p-4", className)}>
      {renderChart()}
    </div>
  );
};

export const loadChart = (
  chartConfig: any,
  chartRefs?: any,
  chartKey?: string
) => {
  if (!chartConfig) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        Loading chart...
      </div>
    );
  }

  const { type, data, options = {} } = chartConfig;
  const chartProps = {
    data,
    options,
    ref: (chartInstance: ChartJS) => {
      chartRefs && (chartRefs.current[chartKey] = chartInstance);
    },
  };

  switch (type) {
    case "line":
      return <Line {...chartProps} />;
    case "bar":
      return <Bar {...chartProps} />;
    case "pie":
      return <Pie {...chartProps} />;
    case "doughnut":
      return <Doughnut {...chartProps} />;
    case "radar":
      return <Radar {...chartProps} />;
    case "polarArea":
      return <PolarArea {...chartProps} />;
    case "scatter":
      return <Scatter {...chartProps} />;
    default:
      return (
        <div className="p-4 text-center text-red-600">
          Unsupported chart type: {type}
        </div>
      );
  }
};

ChartjsDiagram.displayName = "ChartjsDiagram";
